import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Typography } from "@mui/material"
import { translate } from "app/language/service"
import { formatDate } from "app/util/date/tools/common"
import { INotification, INotificationInfo } from "notification/model"
import { getNotificationInfo, getNotificationItem, setNotificationSliderVisibility } from "notification/tools/common"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { ListActionButton } from "ui/components/main-layout/ListActionButton"

interface INotificationItemProps {
  notification: INotification
}

export const NotificationItem = React.memo(({ notification }: INotificationItemProps): JSX.Element => {
  const [info, setInfo] = React.useState<null | INotificationInfo>(null)
  const [hasError, setHasError] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const isCollapsable = info !== null && info.actions.length > 0
  React.useEffect(() => {
    getNotificationInfo(notification)
      .then(newInfo => setInfo(newInfo))
      .catch(error => {
        console.error(error)
        setHasError(true)
      })
  }, [])
  const config = getNotificationItem(notification)
  return (
    <FlexBox
      id={`notification-item-${notification.id}`}
      jc="start"
      ai="start"
      gap
      fullWidth
      onClick={!isCollapsable ? undefined : () => setIsOpen(!isOpen)}
      sx={{
        "position": "relative",
        "pl": "1rem",
        "pr": "1rem",
        "backgroundColor": notification.read ? undefined : theme => theme.palette.background.paper,
        "&:hover": {
          "backgroundColor": theme => theme.palette.action.disabledBackground,
          "cursor": "pointer",
          "& .icon-container": {
            backgroundColor: theme => theme.palette.action.disabledBackground
          }
        }
      }}>
      <FlexBox
        fullHeight
        sx={{ position: "absolute", left: "1.8rem", width: "1px", borderRight: theme => theme.palette.border }}
      />
      <FlexBox
        className="icon-container"
        sx={{
          "position": "absolute",
          "top": ".5rem",
          "left": ".5rem",
          "width": "2.6rem",
          "backgroundColor": theme =>
            notification.read ? theme.palette.background.default : theme.palette.background.paper,
          "zIndex": 1,
          "& .icon": {
            color: config.color,
            fontSize: "1.3rem"
          }
        }}>
        <FlexBox
          sx={{
            width: "2.6rem",
            height: "2.6rem",
            border: theme => theme.palette.border,
            borderRadius: "2.6rem"
          }}>
          <FontAwesomeIcon icon={config.icon} className="icon" />
        </FlexBox>
      </FlexBox>
      <FlexBox fullWidth vertical ai="start" sx={{ ml: "2.6rem", mt: "1.2rem" }}>
        <Typography id={`notification-item-${notification.id}-title`} color="textPrimary" fontSize=".85rem">
          {translate(`notification.type.${notification.type}`)}
        </Typography>
        <Typography
          id={`notification-item-${notification.id}-message`}
          color={hasError ? "error" : "textSecondary"}
          fontSize=".72rem">
          {hasError ? translate("notification.message.error") : info === null ? "" : info.message}
        </Typography>
        {info === null && <LinearLoader />}
        <FlexBox sx={{ mb: ".5rem" }}>
          <Typography id={`notification-item-${notification.id}-date`} color="textSecondary" fontSize=".72rem">
            {formatDate(notification.created_on, { displayTime: true })}
          </Typography>
        </FlexBox>
        {isCollapsable && (
          <FlexBox
            gap
            fullWidth
            jc="end"
            sx={{
              maxHeight: isOpen ? "3rem" : 0,
              overflow: "hidden",
              mb: isOpen ? ".5rem" : 0,
              transition: "all 700ms ease"
            }}>
            {info.actions.map((a, index) => (
              <ListActionButton
                id={`notification-${notification.id}-action-${index}`}
                key={index}
                icon={a.icon}
                bigSize
                onClick={() => {
                  a.onClick()
                  setNotificationSliderVisibility(false)
                }}
                tooltip={a.label}
              />
            ))}
          </FlexBox>
        )}
      </FlexBox>
    </FlexBox>
  )
})
